import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import DatePicker from "@/components/DatePicker";
import { toast } from "sonner";
import { Plus, Trash2, FileDown, Send, Award } from "lucide-react";
import { usd, shortDate } from "@/lib/format";
import { API } from "@/lib/api";

const CONTAINER_TYPES = ["20GP", "40GP", "40HC", "45HC", "20RF", "40RF"];
const INCOTERMS = ["FOB", "EXW", "FCA", "CIF", "CIP", "DAP", "DDP"];
const SUGGESTED_KPIS = [
  "Vessel On-Time Departure ≥ 95%",
  "Booking Confirmation ≤ 24 hrs",
  "Documentation Accuracy ≥ 98%",
  "Rate Validity 12 months with quarterly BAF review",
  "Detention Free Time minimum 14 days at destination",
  "Equipment Availability guaranteed during peak season",
];

export default function Rfq() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [carrierForm, setCarrierForm] = useState({ carrier: "", total_usd: 0, transit_days: 28, free_time_days: 14, reliability_pct: 88, validity: "12 months" });

  const empty = {
    title: "",
    company_name: "Acme Imports Pty Ltd",
    contact_name: "",
    contact_email: "",
    annual_teu: 0,
    period_start: new Date().toISOString().slice(0, 10),
    period_end: new Date(Date.now() + 365 * 86400000).toISOString().slice(0, 10),
    container_types: ["40HC"],
    incoterms: ["FOB"],
    free_time_days: 14,
    target_transit_days: 30,
    reliability_target_pct: 90,
    rate_type: "Contract / NAC",
    validity: "12 months",
    lanes: [{ origin: "", destination: "", annual_teu: 0, container_mix: "40HC", commodity: "" }],
    kpis: [],
    target_carriers: [],
    notes: "",
    carrier_responses: [],
  };
  const [form, setForm] = useState(empty);

  const load = () => api.get("/rfq").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(empty); setActive(null); setOpen(true); };
  const edit = (r) => { setForm({ ...empty, ...r }); setActive(r); setOpen(true); };

  const submit = async () => {
    try {
      if (active) {
        await api.put(`/rfq/${active.id}`, form);
        toast.success("RFQ updated");
      } else {
        await api.post("/rfq", form);
        toast.success("RFQ created");
      }
      setOpen(false); load();
    } catch {
      toast.error("Could not save RFQ");
    }
  };

  const addLane = () => setForm({ ...form, lanes: [...form.lanes, { origin: "", destination: "", annual_teu: 0, container_mix: "40HC", commodity: "" }] });
  const updateLane = (i, key, val) => {
    const lanes = [...form.lanes];
    lanes[i] = { ...lanes[i], [key]: val };
    setForm({ ...form, lanes });
  };
  const removeLane = (i) => setForm({ ...form, lanes: form.lanes.filter((_, idx) => idx !== i) });

  const toggleArray = (key, val) => {
    const arr = form[key] || [];
    setForm({ ...form, [key]: arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val] });
  };

  const addCarrierResp = () => {
    if (!carrierForm.carrier.trim()) return;
    setForm({ ...form, carrier_responses: [...(form.carrier_responses || []), { ...carrierForm }] });
    setCarrierForm({ carrier: "", total_usd: 0, transit_days: 28, free_time_days: 14, reliability_pct: 88, validity: "12 months" });
  };
  const removeCarrierResp = (i) => setForm({ ...form, carrier_responses: form.carrier_responses.filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-6 animate-slide-up" data-testid="rfq-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">RFQ / Tender Builder</h1>
          <p className="text-sm text-zinc-500 mt-1">Run a structured annual tender. Compare carriers. Export PDF for forwarders.</p>
        </div>
        <Button data-testid="new-rfq-btn" onClick={startNew} className="rounded-none bg-[#002FA7] hover:bg-[#002480] h-11"><Plus className="w-4 h-4 mr-2" />New RFQ</Button>
      </div>

      <div className="bg-white border border-zinc-200">
        <div className="px-5 py-4 border-b border-zinc-200">
          <h3 className="font-heading text-lg font-bold tracking-tight">Tenders</h3>
        </div>
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left">
                {["Reference", "Title", "Company", "Annual TEU", "Period", "Carriers Invited", "Responses", ""].map(h => <th key={h} className="px-4 py-2.5 data-label">{h}</th>)}
              </tr>
            </thead>
            <tbody data-testid="rfqs-tbody">
              {items.map(r => (
                <tr key={r.id} className="border-b border-zinc-100 hover:bg-zinc-50/60">
                  <td className="px-4 py-2.5 metric font-medium">{r.reference}</td>
                  <td className="px-4 py-2.5">{r.title}</td>
                  <td className="px-4 py-2.5 text-zinc-600">{r.company_name}</td>
                  <td className="px-4 py-2.5 metric">{r.annual_teu}</td>
                  <td className="px-4 py-2.5 metric text-xs">{shortDate(r.period_start)} → {shortDate(r.period_end)}</td>
                  <td className="px-4 py-2.5 metric">{(r.target_carriers || []).length}</td>
                  <td className="px-4 py-2.5 metric">{(r.carrier_responses || []).length}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex gap-1">
                      <Button data-testid={`edit-rfq-${r.id}`} size="sm" variant="ghost" className="rounded-none h-8" onClick={() => edit(r)}>Edit</Button>
                      <a data-testid={`pdf-rfq-${r.id}`} href={`${API}/rfq/${r.id}/pdf`} target="_blank" rel="noreferrer">
                        <Button size="sm" variant="outline" className="rounded-none border-[#002FA7] text-[#002FA7] hover:bg-[#E6EBF6] h-8"><FileDown className="w-3 h-3 mr-1.5" />PDF</Button>
                      </a>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={8} className="p-10 text-center text-sm text-zinc-500">No tenders yet — create your first RFQ.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="rfq-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{active ? `Edit RFQ — ${active.reference}` : "New RFQ"}</DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">Standard tender structure with lanes, KPIs, carrier list, and a comparison matrix.</DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="brief">
            <TabsList className="rounded-none bg-transparent border-b border-zinc-200 p-0 h-auto w-full justify-start">
              <TabsTrigger value="brief" data-testid="rfq-tab-brief" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-[#002FA7] data-[state=active]:shadow-none data-[state=active]:bg-transparent px-4 py-2">1. Brief</TabsTrigger>
              <TabsTrigger value="lanes" data-testid="rfq-tab-lanes" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-[#002FA7] data-[state=active]:shadow-none data-[state=active]:bg-transparent px-4 py-2">2. Lanes &amp; Volume</TabsTrigger>
              <TabsTrigger value="terms" data-testid="rfq-tab-terms" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-[#002FA7] data-[state=active]:shadow-none data-[state=active]:bg-transparent px-4 py-2">3. KPIs &amp; Terms</TabsTrigger>
              <TabsTrigger value="compare" data-testid="rfq-tab-compare" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-[#002FA7] data-[state=active]:shadow-none data-[state=active]:bg-transparent px-4 py-2">4. Carrier Comparison</TabsTrigger>
            </TabsList>

            <TabsContent value="brief" className="mt-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><Label className="data-label">Tender Title</Label><Input data-testid="rfq-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="rounded-none mt-1.5" placeholder="Annual Asia–AU Ocean Tender 2026" /></div>
                <div><Label className="data-label">Company</Label><Input data-testid="rfq-company" value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} className="rounded-none mt-1.5" /></div>
                <div><Label className="data-label">Annual TEU Volume</Label><Input data-testid="rfq-teu" type="number" value={form.annual_teu} onChange={e => setForm({ ...form, annual_teu: Number(e.target.value) })} className="rounded-none mt-1.5 metric" /></div>
                <div><Label className="data-label">Contact Name</Label><Input data-testid="rfq-contact" value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} className="rounded-none mt-1.5" /></div>
                <div><Label className="data-label">Contact Email</Label><Input data-testid="rfq-email" type="email" value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} className="rounded-none mt-1.5" /></div>
                <div><Label className="data-label">Period Start</Label><DatePicker testId="rfq-start" value={form.period_start} onChange={(v) => setForm({ ...form, period_start: v })} placeholder="Select start date" /></div>
                <div><Label className="data-label">Period End</Label><DatePicker testId="rfq-end" value={form.period_end} onChange={(v) => setForm({ ...form, period_end: v })} placeholder="Select end date" /></div>
              </div>
              <div>
                <Label className="data-label">Container Types</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {CONTAINER_TYPES.map(c => (
                    <button key={c} type="button" data-testid={`rfq-ct-${c}`} onClick={() => toggleArray("container_types", c)}
                      className={`px-3 py-1 text-xs uppercase tracking-[0.12em] border ${form.container_types.includes(c) ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-zinc-700 border-zinc-300 hover:border-[#002FA7]'}`}>{c}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="data-label">Incoterms</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {INCOTERMS.map(i => (
                    <button key={i} type="button" data-testid={`rfq-inco-${i}`} onClick={() => toggleArray("incoterms", i)}
                      className={`px-3 py-1 text-xs uppercase tracking-[0.12em] border ${form.incoterms.includes(i) ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-zinc-700 border-zinc-300 hover:border-[#002FA7]'}`}>{i}</button>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="lanes" className="mt-5 space-y-3">
              <table className="w-full text-sm border border-zinc-200">
                <thead className="bg-zinc-50">
                  <tr className="text-left">
                    {["Origin", "Destination", "Annual TEU", "Mix", "Commodity", ""].map(h => <th key={h} className="px-3 py-2 data-label">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {form.lanes.map((ln, i) => (
                    <tr key={i} className="border-t border-zinc-100">
                      <td className="px-2 py-1"><Input data-testid={`lane-origin-${i}`} value={ln.origin} onChange={e => updateLane(i, "origin", e.target.value)} className="rounded-none border-0 h-9 px-2" placeholder="Shanghai" /></td>
                      <td className="px-2 py-1"><Input data-testid={`lane-dest-${i}`} value={ln.destination} onChange={e => updateLane(i, "destination", e.target.value)} className="rounded-none border-0 h-9 px-2" placeholder="Sydney" /></td>
                      <td className="px-2 py-1"><Input data-testid={`lane-teu-${i}`} type="number" value={ln.annual_teu} onChange={e => updateLane(i, "annual_teu", Number(e.target.value))} className="rounded-none border-0 h-9 px-2 metric" /></td>
                      <td className="px-2 py-1"><Input data-testid={`lane-mix-${i}`} value={ln.container_mix} onChange={e => updateLane(i, "container_mix", e.target.value)} className="rounded-none border-0 h-9 px-2" /></td>
                      <td className="px-2 py-1"><Input data-testid={`lane-com-${i}`} value={ln.commodity} onChange={e => updateLane(i, "commodity", e.target.value)} className="rounded-none border-0 h-9 px-2" /></td>
                      <td className="px-2 py-1 text-right"><Button data-testid={`lane-del-${i}`} variant="ghost" size="icon" className="rounded-none h-8 w-8" onClick={() => removeLane(i)}><Trash2 className="w-3.5 h-3.5 text-[#991B1B]" /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Button data-testid="add-lane-btn" variant="outline" className="rounded-none border-zinc-300" onClick={addLane}><Plus className="w-3.5 h-3.5 mr-1.5" />Add lane</Button>
            </TabsContent>

            <TabsContent value="terms" className="mt-5 space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div><Label className="data-label">Free Time (days)</Label><Input data-testid="rfq-free-time" type="number" value={form.free_time_days} onChange={e => setForm({ ...form, free_time_days: Number(e.target.value) })} className="rounded-none mt-1.5 metric" /></div>
                <div><Label className="data-label">Target Transit (days)</Label><Input data-testid="rfq-transit" type="number" value={form.target_transit_days} onChange={e => setForm({ ...form, target_transit_days: Number(e.target.value) })} className="rounded-none mt-1.5 metric" /></div>
                <div><Label className="data-label">Reliability Target %</Label><Input data-testid="rfq-rel" type="number" value={form.reliability_target_pct} onChange={e => setForm({ ...form, reliability_target_pct: Number(e.target.value) })} className="rounded-none mt-1.5 metric" /></div>
                <div>
                  <Label className="data-label">Rate Type</Label>
                  <Select value={form.rate_type} onValueChange={v => setForm({ ...form, rate_type: v })}>
                    <SelectTrigger data-testid="rfq-rate-type" className="rounded-none mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>{["Contract / NAC", "FAK", "Spot", "Hybrid Contract+Spot"].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="data-label">Validity</Label><Input data-testid="rfq-validity" value={form.validity} onChange={e => setForm({ ...form, validity: e.target.value })} className="rounded-none mt-1.5" /></div>
              </div>
              <div>
                <Label className="data-label">Suggested KPIs (click to toggle)</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {SUGGESTED_KPIS.map(k => (
                    <button key={k} type="button" data-testid={`rfq-kpi-${k.slice(0,15)}`} onClick={() => toggleArray("kpis", k)}
                      className={`px-3 py-1.5 text-xs text-left border ${form.kpis.includes(k) ? 'bg-[#E6EBF6] text-[#002FA7] border-[#002FA7]' : 'bg-white text-zinc-700 border-zinc-300 hover:border-[#002FA7]'}`}>{k}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="data-label">Target Carriers (comma separated)</Label>
                <Input data-testid="rfq-carriers" value={(form.target_carriers || []).join(", ")} onChange={e => setForm({ ...form, target_carriers: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} className="rounded-none mt-1.5" placeholder="Maersk, MSC, CMA CGM, ONE" />
              </div>
              <div>
                <Label className="data-label">Notes</Label>
                <Textarea data-testid="rfq-notes" value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} rows={4} className="rounded-none mt-1.5" />
              </div>
            </TabsContent>

            <TabsContent value="compare" className="mt-5 space-y-4">
              <div className="border border-zinc-200 p-4 bg-zinc-50">
                <div className="data-label mb-3">Add Carrier Response</div>
                <div className="grid grid-cols-6 gap-2">
                  <Input data-testid="cr-carrier" placeholder="Carrier" value={carrierForm.carrier} onChange={e => setCarrierForm({ ...carrierForm, carrier: e.target.value })} className="rounded-none h-9" />
                  <Input data-testid="cr-total" type="number" placeholder="Total USD" value={carrierForm.total_usd} onChange={e => setCarrierForm({ ...carrierForm, total_usd: Number(e.target.value) })} className="rounded-none h-9 metric" />
                  <Input data-testid="cr-transit" type="number" placeholder="Transit d" value={carrierForm.transit_days} onChange={e => setCarrierForm({ ...carrierForm, transit_days: Number(e.target.value) })} className="rounded-none h-9 metric" />
                  <Input data-testid="cr-free" type="number" placeholder="Free time d" value={carrierForm.free_time_days} onChange={e => setCarrierForm({ ...carrierForm, free_time_days: Number(e.target.value) })} className="rounded-none h-9 metric" />
                  <Input data-testid="cr-rel" type="number" placeholder="Reliability %" value={carrierForm.reliability_pct} onChange={e => setCarrierForm({ ...carrierForm, reliability_pct: Number(e.target.value) })} className="rounded-none h-9 metric" />
                  <Button data-testid="cr-add" onClick={addCarrierResp} className="rounded-none bg-[#002FA7] hover:bg-[#002480] h-9"><Plus className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
              <table className="w-full text-sm border border-zinc-200">
                <thead className="bg-zinc-50">
                  <tr className="text-left">
                    {["Carrier", "Total USD / 40HC", "Transit", "Free Time", "Reliability", "Validity", ""].map(h => <th key={h} className="px-3 py-2 data-label">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {(form.carrier_responses || []).map((r, i) => {
                    const cheapest = Math.min(...form.carrier_responses.map(x => x.total_usd));
                    return (
                      <tr key={i} className="border-t border-zinc-100">
                        <td className="px-3 py-2 font-medium">{r.carrier}</td>
                        <td className={`px-3 py-2 metric ${r.total_usd === cheapest ? 'text-[#166534] font-bold' : ''}`}>{usd(r.total_usd)} {r.total_usd === cheapest && <Award className="w-3 h-3 inline ml-1" />}</td>
                        <td className="px-3 py-2 metric">{r.transit_days}d</td>
                        <td className="px-3 py-2 metric">{r.free_time_days}d</td>
                        <td className="px-3 py-2 metric">{r.reliability_pct}%</td>
                        <td className="px-3 py-2 text-xs">{r.validity}</td>
                        <td className="px-3 py-2 text-right"><Button data-testid={`cr-del-${i}`} variant="ghost" size="icon" className="rounded-none h-7 w-7" onClick={() => removeCarrierResp(i)}><Trash2 className="w-3 h-3 text-[#991B1B]" /></Button></td>
                      </tr>
                    );
                  })}
                  {form.carrier_responses.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-xs text-zinc-500">Enter at least 2 carrier responses to see comparison.</td></tr>}
                </tbody>
              </table>
            </TabsContent>
          </Tabs>

          <div className="flex justify-end gap-2 mt-4 border-t border-zinc-200 pt-4">
            {active && <a data-testid="rfq-export-pdf" href={`${API}/rfq/${active.id}/pdf`} target="_blank" rel="noreferrer"><Button variant="outline" className="rounded-none border-zinc-300"><FileDown className="w-4 h-4 mr-2" />Export PDF</Button></a>}
            <Button data-testid="rfq-save" onClick={submit} className="rounded-none bg-[#002FA7] hover:bg-[#002480]"><Send className="w-4 h-4 mr-2" />{active ? "Update" : "Create RFQ"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
